/**
 * Which consultation manual the reference pane should open on.
 *
 * When a clinician opens the intervention panel, the pane does not make them hunt for the manual they are most
 * likely to need: it opens on the 항목 the patient is doing WORST on, at that patient's own grade. The rule is
 * the one the owning admin document states — the manual matching the patient's PRO grade is what gets put in
 * front of the clinician.
 *
 * "Worst" = the highest grade the patient has been given (단계 3 beats 단계 2 beats 단계 1), and where two items
 * tie, the more recent week wins — a patient graded 3 last week matters more than one graded 3 in week two.
 *
 * If nothing can be picked (no PRO responses yet, or no manual published for that 항목), the pane simply opens on
 * the full tree. Preselection is a convenience; it never hides anything, and the clinician can always browse.
 */
import type { ManualGradeCode, ManualItem, ManualTree } from "@/api/adapters/patients";
import type { ProResponseMap } from "@/features/patients/pro-responses";

/** The patient's worst-graded PRO item: which item, at which grade, from which week. */
export interface WorstProItem {
	proItemId: number;
	grade: 1 | 2 | 3;
	week: number;
}

/** 1/2/3 (what the PRO grid speaks) → G1/G2/G3 (what the manual wire speaks). */
export function gradeCode(grade: 1 | 2 | 3): ManualGradeCode {
	return `G${grade}` as ManualGradeCode;
}

/**
 * The patient's worst-graded PRO item, or null when the patient has no graded response at all.
 *
 * The grid is keyed `${proItemId}:${weekIndex}`; a cell with no grade (missed, not yet due, answered but not
 * scored) is not a signal about severity and is skipped rather than treated as a good result.
 */
export function pickWorstProItem(grid: ProResponseMap | undefined): WorstProItem | null {
	if (!grid) return null;

	let worst: WorstProItem | null = null;
	for (const [key, response] of grid) {
		if (response.grade == null) continue;

		const [itemPart, weekPart] = key.split(":");
		const proItemId = Number(itemPart);
		const week = Number(weekPart);
		if (!Number.isFinite(proItemId) || !Number.isFinite(week)) continue;

		const candidate: WorstProItem = { proItemId, grade: response.grade, week };
		if (
			worst === null ||
			candidate.grade > worst.grade ||
			(candidate.grade === worst.grade && candidate.week > worst.week)
		) {
			worst = candidate;
		}
	}
	return worst;
}

/** The tree node published against a PRO item, or null when nobody has written a manual for it. */
export function findManualForProItem(
	tree: ManualTree,
	proItemId: number,
): ManualItem | null {
	for (const area of tree) {
		for (const sub of area.subAreas) {
			for (const item of sub.items) {
				if (item.proItemId === proItemId) return item;
			}
		}
	}
	return null;
}

/** What the pane should open on: the manual for the patient's worst PRO item, at that patient's grade. */
export interface ManualPreselection {
	item: ManualItem;
	grade: ManualGradeCode;
}

/**
 * Resolve the preselection, or null to open on the full tree.
 *
 * The grade tab follows the patient's own grade — but only when that grade has actually been authored. A patient
 * graded 단계 3 on an item whose 단계 3 manual nobody has written yet falls back to the item's lowest published
 * grade, so the clinician still lands on readable content instead of an empty "not yet prepared" tab.
 */
export function resolveManualPreselection(
	tree: ManualTree,
	grid: ProResponseMap | undefined,
): ManualPreselection | null {
	const worst = pickWorstProItem(grid);
	if (!worst) return null;

	const item = findManualForProItem(tree, worst.proItemId);
	if (!item || item.grades.length === 0) return null;

	const wanted = gradeCode(worst.grade);
	const exact = item.grades.some((g) => g.grade === wanted);
	return { item, grade: exact ? wanted : item.grades[0].grade };
}
